import {
  Box,
  Typography,
  Paper,
  Stack,
  Divider,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useTranslation } from 'react-i18next';
import { useCollectionByFirm } from '../../../../hooks/useCollectionByFirm';
import { CommentSection } from '../../../../components';
import { formatCurrency } from '../../../../constants/tableConfigurations';
import { formatDateShort } from '../../../../utils/dateUtils';

const AmountRow = ({ label, value, color, large }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between">
    <Typography variant="caption" color="text.secondary" fontWeight={500}>{label}</Typography>
    <Typography
      fontWeight={large ? 800 : 600}
      color={color || 'text.primary'}
      sx={{ fontSize: large ? '1rem' : '0.82rem', fontFamily: 'monospace' }}
    >
      {value}
    </Typography>
  </Stack>
);

const SectionLabel = ({ icon, children }) => (
  <Stack direction="row" alignItems="center" gap={0.5} mb={0.75}>
    {icon}
    <Typography variant="caption" color="text.disabled" fontWeight={700} textTransform="uppercase" letterSpacing={0.8} sx={{ fontSize: 10 }}>
      {children}
    </Typography>
  </Stack>
);

const CollectionCard = ({ firmId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { collection, loading } = useCollectionByFirm(firmId);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }

  if (!collection) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
          <Typography variant="subtitle2" fontWeight={700}>{t('tahsilat.title', 'Tahsilat')}</Typography>
        </Box>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <AccountBalanceIcon sx={{ fontSize: 32, color: 'text.disabled', opacity: 0.3, mb: 1 }} />
          <Typography variant="body2" color="text.disabled">{t('tahsilat.noRecord', 'Tahsilat kaydı bulunamadı.')}</Typography>
          <Typography variant="caption" color="text.disabled">{t('tahsilat.importHint', 'Luca raporu import edince burada görünecek.')}</Typography>
        </Box>
      </Paper>
    );
  }

  const bakiyeColor = Number(collection.tl_bakiye) > 0
    ? theme.palette.error.main
    : Number(collection.tl_bakiye) < 0
      ? theme.palette.success.dark
      : theme.palette.text.primary;

  const reminders = collection.reminders || [];
  const promises = collection.payment_promises || [];

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 1.75,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.text.primary, 0.02),
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        <Typography variant="subtitle2" fontWeight={700}>{t('tahsilat.title', 'Tahsilat')}</Typography>
        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
          #{collection.luca_code}
        </Typography>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/* Amounts */}
        <Stack gap={1}>
          <AmountRow label={t('tahsilat.tlBor', 'TL Borç')} value={formatCurrency(collection.tl_bor)} color="error.main" />
          <AmountRow label={t('tahsilat.tlAlacak', 'TL Alacak')} value={formatCurrency(collection.tl_alacak)} color="success.dark" />
          <Divider sx={{ my: 0.5 }} />
          <AmountRow label={t('tahsilat.tlBakiye', 'Net Bakiye')} value={formatCurrency(collection.tl_bakiye)} color={bakiyeColor} large />
        </Stack>

        <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: 'block', fontSize: 10 }}>
          {t('tahsilat.importDate', 'Son import')}: {formatDateShort(collection.import_date)}
        </Typography>

        {/* Reminders */}
        {reminders.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionLabel icon={<NotificationsNoneIcon sx={{ fontSize: 13 }} />}>
              {t('tahsilat.reminders', 'Hatırlatıcılar')}
            </SectionLabel>
            <Stack gap={0.75}>
              {reminders.map(r => (
                <Box key={r.id} sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 1.5, py: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ fontSize: 11 }}>
                    {formatDateShort(r.reminder_date)}
                  </Typography>
                  {r.note && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 11 }}>{r.note}</Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* Payment Promises */}
        {promises.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionLabel icon={<HandshakeIcon sx={{ fontSize: 13 }} />}>
              {t('tahsilat.paymentPromises', 'Ödeme Sözleri')}
            </SectionLabel>
            <Stack gap={0.75}>
              {promises.map(p => (
                <Box key={p.id} sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 1.5, py: 1 }}>
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="caption" fontWeight={700} color="success.main" sx={{ fontSize: 11 }}>
                      {formatDateShort(p.promise_date)}
                    </Typography>
                    {p.amount != null && (
                      <Chip
                        onClick={ () => {}}
                        label={formatCurrency(p.amount)}
                        size="small"
                        sx={{ height: 16, fontSize: 10, fontFamily: 'monospace', fontWeight: 700, bgcolor: theme => `${theme.palette.success.main}18`, color: 'success.main', border: 'none' }}
                      />
                    )}
                    {p.promised_by && (
                      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: 11 }}>{p.promised_by}</Typography>
                    )}
                  </Stack>
                  {p.note && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 11 }}>{p.note}</Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* Comments */}
        <Divider sx={{ my: 2 }} />
        <CommentSection entityType="collection" entityId={collection.id} />
      </Box>
    </Paper>
  );
};

export default CollectionCard;
